import {
  ComponentType,
  CSSProperties,
  PropsWithChildren,
  RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { BottomNavigationContextType, BottomNavigationProvider } from './bottom-navigation.context';
import styles from './bottom-navigation.module.css';
import { Link } from './components/link/link';
import { Search } from './components/search/search';
import { SideNavigation } from './components/side-navigation/side-navigation';

const useHideOnScroll = (ref: RefObject<HTMLElement>) => {
  const [bottomOffset, setBottomOffset] = useState(0);
  const [pageYOffset, setPageYOffset] = useState(0);
  const [lastPageYOffset, setLastPageYOffset] = useState(0);

  const handleScroll = useCallback(() => {
    if (window.scrollY >= 0 && window.scrollY <= document.body.scrollHeight - window.innerHeight) {
      setPageYOffset(window.scrollY);
    }
  }, [setPageYOffset]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const newBottomOffset = pageYOffset > lastPageYOffset ? ref?.current?.offsetHeight || 0 : 0;
    if (newBottomOffset !== bottomOffset) {
      setBottomOffset(newBottomOffset);
    }
    setLastPageYOffset(pageYOffset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageYOffset]);

  return {
    bottomOffset,
  };
};

type BottomNavigationComposition = {
  Link: typeof Link;
  Search: typeof Search;
  SideNavigation: typeof SideNavigation;
};

type BottomNavigationProps = PropsWithChildren<
  Omit<BottomNavigationContextType, 'linkFactory'> & Partial<Pick<BottomNavigationContextType, 'linkFactory'>>
> & {
  className?: string;
};
export const BottomNavigation = (({ children, className = '', ...props }: BottomNavigationProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const { bottomOffset } = useHideOnScroll(ref);

  return (
    <BottomNavigationProvider {...props}>
      <div
        className={`${styles.wrapper} ${className}`}
        ref={ref}
        style={
          {
            '--bottom-navigation-translate-y': `${bottomOffset}px`,
          } as CSSProperties
        }
      >
        {children}
      </div>
    </BottomNavigationProvider>
  );
}) as ComponentType<BottomNavigationProps> & BottomNavigationComposition;
BottomNavigation.Link = Link;
BottomNavigation.Search = Search;
BottomNavigation.SideNavigation = SideNavigation;
